import axios from "axios";
import { useAuth } from "react-oidc-context";
import { generatePath } from "react-router-dom";
import React, { forwardRef, useCallback, useMemo, useState } from "react";
import cx from "classnames";

import { CarriersListResponse } from "models/dto/CarriersListResponse";
import { SelectedCarrier } from "models/SelectedCarrier";
import routerList from "routes/routerList";
import { QueryGridResponse } from "models/QueryGridResponse";
import { InfiniteKeyedMutator } from "hooks/useGetCarriersInfinite";
import CarrierListRowCellsLarge from "./CarrierListRowCellsLarge";
import CarrierListRowCellsSmall from "./CarrierListRowCellsSmall";
import {
  allCarrierListColumnKeys,
  CarrierListColumnKey,
} from "./carrierListColumns";
import { getCarrierCellValues } from "./utils/carrierListUtilities";

import styles from "../CarrierList.module.scss";

type CarrierListRowProps = {
  selectedCarrier: SelectedCarrier | null;
  carrier: CarriersListResponse;
  onRowClick: () => void;
  isFullSize: boolean;
  visibleColumns?: CarrierListColumnKey[];
  reloadCarriers: InfiniteKeyedMutator<
    QueryGridResponse<CarriersListResponse>[]
  >;
};

export type CarrierListRowCellsSizeProps = {
  carrier: CarriersListResponse;
  isOwnedByCurrentUser: boolean;
  visibleColumns: CarrierListColumnKey[];
  handleCarrierEdit: (event: React.MouseEvent) => void;
  handleCarrierStar: (event: React.MouseEvent) => void;
  cellValues: {
    carrierName: string;
    carrierOwner: string;
    phone: string;
    email: string;
    address: string;
    country: string;
    fleetSize: string;
    shippeoOnboarded: string;
    lastActivityTime: string;
    lastShipmentDelivered: string;
    lastActivitySummary: string;
    lastActivityNote: string;
  };
  isLoadingStarredStatus: boolean;
};

const CarrierListRow = forwardRef<HTMLTableRowElement, CarrierListRowProps>(
  (props, ref) => {
    const { user } = useAuth();
    const userFullName = user
      ? `${user.profile.firstName} ${user.profile.lastName}`
      : "";
    const isSelectedCarrier = props.selectedCarrier?.id === props.carrier.id;
    const isOwnedByCurrentUser = props.carrier.owner?.name === userFullName;

    const [isLoadingStarredStatus, setIsLoadingStarredStatus] =
      useState<boolean>(false);

    const {
      reloadCarriers,
      carrier: { id: carrierId },
    } = props;

    // When the user stars/unstars a carrier, optimistically show the star change while the updated carrier list is being revalidated (better UX)
    const optimisticData = useCallback(
      (
        _?: QueryGridResponse<CarriersListResponse>[],
        displayedData?: QueryGridResponse<CarriersListResponse>[]
      ) => {
        if (!displayedData) {
          return [];
        }

        return displayedData.map((carrierResponse) => {
          const foundCarrier = carrierResponse.entities.find(
            (carrier) => carrier.id === carrierId
          );

          return foundCarrier
            ? {
                ...carrierResponse,
                entities: carrierResponse.entities.map((carrier) =>
                  carrier.id === carrierId
                    ? {
                        ...carrier,
                        isFavoritedByCurrentUser:
                          !carrier.isFavoritedByCurrentUser,
                      }
                    : carrier
                ),
              }
            : carrierResponse;
        });
      },
      [carrierId]
    );

    const handleCarrierEdit = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        window.open(
          generatePath(routerList.carriers.details, { carrierId }),
          "_blank",
          "noopener,noreferrer"
        );
      },
      [carrierId]
    );

    const handleCarrierStar = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        setIsLoadingStarredStatus(true);

        axios
          .post(`/api/carriers/${carrierId}/star`, { carrierId })
          .then(() => {
            reloadCarriers(undefined, {
              rollbackOnError: true,
              populateCache: false,
              optimisticData,
            });
          })
          .finally(() => setIsLoadingStarredStatus(false));
      },
      [carrierId, reloadCarriers, optimisticData]
    );

    const cellValues = useMemo(
      () => getCarrierCellValues(props.carrier),
      [props.carrier]
    );

    const cellProps: CarrierListRowCellsSizeProps = {
      carrier: props.carrier,
      isOwnedByCurrentUser,
      visibleColumns: props.visibleColumns ?? allCarrierListColumnKeys,
      handleCarrierEdit,
      handleCarrierStar,
      cellValues,
      isLoadingStarredStatus,
    };

    return (
      <tr
        ref={isSelectedCarrier ? ref : null}
        onClick={props.onRowClick}
        className={cx(isSelectedCarrier && styles.activeRow)}
      >
        {props.isFullSize ? (
          <CarrierListRowCellsLarge {...cellProps} />
        ) : (
          <CarrierListRowCellsSmall {...cellProps} />
        )}
      </tr>
    );
  }
);

export default CarrierListRow;
